export function registerAlpineComponents(Alpine) {
  Alpine.data("haStagger", (step = 120) => ({
    init() {
      const observed = new WeakSet();
      const intersectionObserver = new IntersectionObserver((entries, obs) => {
        const revealing = entries.filter((e) => e.isIntersecting);
        revealing.forEach((entry, i) => {
          entry.target.style.transitionDelay = `${i * step}ms`;
          entry.target.classList.add("in-view");
          obs.unobserve(entry.target);
        });
      }, { threshold: 0.3 });

      const observeNew = (root) => {
        const items = root.matches?.(".stagger-item")
          ? [root]
          : Array.from(root.querySelectorAll?.(".stagger-item") ?? []);
        items.forEach((item) => {
          if (observed.has(item)) return;
          observed.add(item);
          intersectionObserver.observe(item);
        });
      };

      observeNew(this.$el);

      const mutationObserver = new MutationObserver((mutations) => {
        mutations.forEach((m) => {
          m.addedNodes.forEach((node) => {
            if (node.nodeType === 1) observeNew(node);
          });
        });
      });
      mutationObserver.observe(this.$el, { childList: true, subtree: true });
    },
  }));

  Alpine.data("haDisclosure", (initialOpen = false) => ({
    open: initialOpen,
    toggle() {
      this.open = !this.open;
    },
    close() {
      this.open = false;
    },
  }));

  Alpine.data("haDropdown", () => ({
    open: false,
    activeIndex: -1,
    items: [],
    init() {
      this.refreshItems();
    },
    refreshItems() {
      this.items = Array.from(
        this.$refs.panel?.querySelectorAll(
          'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])',
        ) || [],
      );
    },
    toggle() {
      this.open ? this.close() : this.show();
    },
    show() {
      this.open = true;
      this.$nextTick(() => this.refreshItems());
    },
    close() {
      this.open = false;
      this.activeIndex = -1;
    },
    focusNext() {
      if (!this.items.length) {
        return;
      }
      this.activeIndex = (this.activeIndex + 1) % this.items.length;
      this.items[this.activeIndex].focus();
    },
    focusPrevious() {
      if (!this.items.length) {
        return;
      }
      this.activeIndex =
        this.activeIndex <= 0 ? this.items.length - 1 : this.activeIndex - 1;
      this.items[this.activeIndex].focus();
    },
  }));

  Alpine.data("haTabs", (activeTab = null) => ({
    activeTab,
    init() {
      if (!this.activeTab) {
        this.activeTab =
          this.$el.querySelector("[data-ha-tab]")?.dataset.haTab || null;
      }
    },
    select(tab) {
      this.activeTab = tab;
    },
    isSelected(tab) {
      return this.activeTab === tab;
    },
  }));

  Alpine.data("haDialog", (initialOpen = false) => ({
    open: initialOpen,
    show() {
      this.open = true;
    },
    close() {
      this.open = false;
    },
  }));

  Alpine.data("haThemeToggle", () => ({
    isDark: false,
    init() {
      this.isDark = document.documentElement.classList.contains("dark");
    },
    toggle() {
      this.isDark = !this.isDark;
      document.documentElement.classList.toggle("dark", this.isDark);
      localStorage.setItem("theme", this.isDark ? "dark" : "light");
    },
  }));

  Alpine.data("haStickyHeader", (threshold = 8) => ({
    scrolled: false,
    init() {
      this.update();
      window.addEventListener("scroll", this.update.bind(this), {
        passive: true,
      });
    },
    update() {
      this.scrolled = window.scrollY > threshold;
    },
  }));

  Alpine.data("haTopLoader", () => ({
    requests: 0,
    progress: 0,
    visible: false,
    completionEvents: [
      "htmx:afterSettle",
      "htmx:responseError",
      "htmx:sendError",
      "htmx:timeout",
    ],
    trickleTimer: null,
    finishTimer: null,
    resetTimer: null,

    init() {
      this.startRequest = this.startRequest.bind(this);
      this.finishRequest = this.finishRequest.bind(this);

      document.body.addEventListener("htmx:beforeRequest", this.startRequest);
      this.completionEvents.forEach((eventName) => {
        document.body.addEventListener(eventName, this.finishRequest);
      });
    },

    destroy() {
      this.clearTimers();
      document.body.removeEventListener("htmx:beforeRequest", this.startRequest);
      this.completionEvents.forEach((eventName) => {
        document.body.removeEventListener(eventName, this.finishRequest);
      });
    },

    startRequest() {
      this.requests += 1;
      this.clearTimers();

      if (!this.visible || this.progress >= 100) {
        this.progress = 8;
      }

      this.visible = true;

      if (!this.trickleTimer) {
        this.trickleTimer = window.setInterval(() => this.trickle(), 220);
      }
    },

    finishRequest() {
      this.requests = Math.max(0, this.requests - 1);

      if (this.requests > 0) {
        return;
      }

      window.clearInterval(this.trickleTimer);
      this.trickleTimer = null;
      this.progress = 100;

      this.finishTimer = window.setTimeout(() => {
        this.visible = false;

        this.resetTimer = window.setTimeout(() => {
          if (!this.visible) {
            this.progress = 0;
          }
        }, 220);
      }, 350);
    },

    trickle() {
      if (this.progress < 80) {
        this.progress += Math.random() * 9;
        return;
      }

      if (this.progress < 94) {
        this.progress += Math.random() * 2;
      }
    },

    clearTimers() {
      window.clearInterval(this.trickleTimer);
      window.clearTimeout(this.finishTimer);
      window.clearTimeout(this.resetTimer);
      this.trickleTimer = null;
    },
  }));
}
